import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'Rajesh Malhotra',
      project: 'Luxury Villa, Alibaug',
      rating: 5,
      content:
        'Venara Estate transformed our search for a second home into an extraordinary experience. Their understanding of our lifestyle needs and impeccable taste led us to a stunning coastal villa that exceeded all expectations.',
    },
    {
      name: 'Priya Desai',
      project: 'Penthouse, South Mumbai',
      rating: 5,
      content:
        'The level of professionalism and market knowledge demonstrated by the Venara team is unparalleled. They secured us an off-market penthouse with panoramic sea views at an exceptional price point.',
    },
    {
      name: 'Ahmed Al-Rashid',
      project: 'Investment Portfolio, Dubai',
      rating: 5,
      content:
        'As an international investor, I needed guidance I could trust. Venara Estate provided comprehensive market intelligence and seamless transaction support across borders. Truly world-class service.',
    },
    {
      name: 'Anjali & Vikram Sharma',
      project: 'Hill Station Retreat, Lonavala',
      rating: 5,
      content:
        'From the first consultation to the final handover, every interaction was marked by attention to detail and genuine care. Our Lonavala retreat is now our family\'s most cherished sanctuary.',
    },
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-accent" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-secondary text-sm font-medium tracking-widest uppercase">Client Testimonials</span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-primary mt-4 mb-6 tracking-tight">
            Stories of Distinction
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Hear from discerning clients who entrusted us with their luxury real estate journeys.
          </p>
        </motion.div>

        {/* Testimonial Card */}
        <div className="relative">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.6 }}
            className="bg-white p-12 border border-secondary/20 relative"
            data-testid="testimonial-card"
          >
            {/* Quote Mark */}
            <div className="absolute top-8 left-8 text-secondary/20 font-playfair text-8xl leading-none">"</div>

            {/* Stars */}
            <div className="flex gap-1 mb-6 relative z-10">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <Star key={i} size={20} className="text-secondary fill-secondary" data-testid="star-icon" />
              ))}
            </div>

            {/* Content */}
            <p className="text-primary text-lg leading-relaxed mb-8 relative z-10 font-light">
              {currentTestimonial.content}
            </p>

            {/* Author */}
            <div className="relative z-10">
              <div className="font-playfair text-xl font-semibold text-primary">
                {currentTestimonial.name}
              </div>
              <div className="text-muted-foreground text-sm mt-1">{currentTestimonial.project}</div>
            </div>
          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              data-testid="testimonial-prev-button"
              className="w-12 h-12 border border-primary/20 hover:border-secondary flex items-center justify-center transition-all duration-300 group"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} className="text-primary group-hover:text-secondary transition-colors duration-300" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  data-testid={`testimonial-dot-${index}`}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex ? 'bg-secondary w-8' : 'bg-primary/20'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              data-testid="testimonial-next-button"
              className="w-12 h-12 border border-primary/20 hover:border-secondary flex items-center justify-center transition-all duration-300 group"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} className="text-primary group-hover:text-secondary transition-colors duration-300" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
